
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { useCallback, useEffect, useMemo } from "react";
import { parseOrganizationClients } from "../../../utils/parsers";
import { initializeClients } from "../../../store/actions";
import { Table } from "../../base";
import { TableProps } from "antd/lib/table";
import { ActionMenu } from "./cell-renders";
import { Grid, Card, Row, Col, Typography, Empty, Skeleton, Tag } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import "styles/components/tab-content/client-content/client-content.scss";

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

// Desktop/Tablet Table Columns
const getColumns = (isTablet: boolean): TableProps<any>["columns"] => {
  const columns: TableProps<any>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      width: isTablet ? 140 : 170,
      ellipsis: true,
      fixed: isTablet ? "left" : undefined,
    },
    {
      title: "Address",
      dataIndex: "address",
      width: isTablet ? 120 : 130,
      ellipsis: true,
      responsive: ["md"] as any,
    },
    {
      title: "City",
      dataIndex: "city",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["lg"] as any,
    },
    {
      title: "Province",
      dataIndex: "state",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["lg"] as any,
    },
    {
      title: "Country",
      dataIndex: "country",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["md"] as any,
    },
    {
      title: "",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      width: isTablet ? 50 : 56,
      fixed: "right",
      className: "action-menu open-link --issues",
    },
  ];

  return columns;
};

// Client Card Component for Mobile
interface ClientCardProps {
  client: any;
}

const ClientCard = ({ client }: ClientCardProps) => {
  return (
    <Card className="client-card" size="small">
      {/* Action Button - Top Right Corner */}
      <div className="card-action-corner" onClick={(e) => e.stopPropagation()}>
        {ActionMenu("", client)}
      </div>

      {/* Card Content */}
      <div className="card-content">
        {/* Client Name */}
        <div className="client-header">
          <Title level={5} className="client-name">
            {client.name || "Unnamed Client"}
          </Title>
        </div>

        {/* Address Section */}
        <div className="address-section">
          <div className="address-icon">
            <EnvironmentOutlined />
          </div>
          <div className="address-details">
            {client.address && (
              <Text className="address-line">{client.address}</Text>
            )}
            <Text className="location-line">
              {[client.city, client.state, client.country]
                .filter(Boolean)
                .join(", ") || "Location not specified"}
            </Text>
          </div>
        </div>

        {/* Tags/Stats Section (Optional) */}
        {(client.vehicle_count || client.organization_type) && (
          <div className="card-footer">
            {client.vehicle_count && (
              <Tag color="blue">{client.vehicle_count} Vehicles</Tag>
            )}
            {client.organization_type && (
              <Tag color="green">{client.organization_type}</Tag>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// Loading Skeleton for Mobile
const MobileSkeleton = () => (
  <Row gutter={[12, 12]}>
    {[1, 2, 3, 4].map((item) => (
      <Col xs={24} sm={12} key={item}>
        <Card className="client-card">
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      </Col>
    ))}
  </Row>
);

// Mobile List View
interface MobileViewProps {
  data: any[];
  loading?: boolean;
}

const MobileView = ({ data, loading }: MobileViewProps) => {
  if (loading) {
    return <MobileSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Empty
          description="No clients found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="mobile-clients-list">
      <Row gutter={[12, 12]}>
        {data.map((client, index) => (
          <Col xs={24} sm={12} key={client.id || client.key || index}>
            <ClientCard client={client} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Main B2CTableView Component
export default function B2CTableView() {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const isTablet = !!(screens.md && !screens.lg);

  const organizationsState = useSelector(
    (state: AppState) => state.organizations
  );
  const {
    organizations,
    page,
    search,
    filters: { listType },
    selectedOrganizationsRows,
  } = organizationsState;
  const loading = (organizationsState as any).loading ?? false;

  const { selectedPage, totalPages } = page;

  const data = useMemo(
    () => parseOrganizationClients(search, organizations),
    [organizations, listType, search]
  );

  useEffect(() => {
    dispatch(initializeClients(2, listType));
  }, [dispatch, listType]);

  const onChangeSelection = useCallback((value) => {
    console.log("values : ", value);
  }, []);

  const columns = useMemo(() => getColumns(isTablet), [isTablet]);

  const getScrollConfig = () => {
    if (isTablet) {
      return { y: "65vh", x: 500 };
    }
    return { y: "70vh" };
  };

  return (
    <>
      <div className="clients-table-container">
        {isMobile ? (
          <MobileView data={data} loading={loading} />
        ) : (
          <Table
            className="clients-table"
            columns={columns}
            dataSource={data}
            pagination={false}
         //   scroll={getScrollConfig()}
            size={isTablet ? "small" : "middle"}
            rowKey={(record) => record.id || record.key}
          />
        )}
      </div>
    </>
  );
}